import { computed, watch } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { locationFacetValue, locationLabel } from '@/composables/records/recordsFormat';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, useStatusRecords } from '@/plugins/store/actions/queries/status';
import { RECORD_ACTIONS_COLUMN_ID, type StatusRow } from '@/types';
import { columnPresence, isColumnAvailable, TOGGLE_COLUMN_IDS, type StatusToggleColumnId } from './useStatusColumnVisibility';

const HEADINGS_KEY = 'views.pigs.records.table.headings';

/** i18n key for Section's heading, read off the same shared `records` namespace every other records
 * page's headings live under. */
const HEADING_KEYS: Record<StatusToggleColumnId, string> = {
    section: `${HEADINGS_KEY}.section`,
    created_by: `${HEADINGS_KEY}.createdBy`,
};

/**
 * What the Status page filters on: the Location column, faceted on the barn id the same way
 * Sold/Slaughters' own Location column is (a name repeats across farms, an id does not), plus
 * Section, presence-gated the same way Slaughters' own Section facet is.
 */
function facets(): RecordFacetDefinitions<StatusRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        barn: {
            value: (row) => locationFacetValue(row.farm, row.barn),
            label: (row) => locationLabel(row.farm, row.barn) || blank,
        },
        section: {
            value: (row) => row.section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.section?.name ?? blank,
        },
    };
}

/**
 * The columns, in the order a status registration reads: the registration date, where it happened
 * and how many pigs, then - when the loaded rows carry it - Section, then the two weight figures.
 * `avg_weight`/`total_weight` are sortable via `accessorKey`; Location/Section are filterable via
 * `meta.filterable` - never both, the same split every other records page's own filterable columns
 * already draw.
 */
function columns(isVisible: (columnId: StatusToggleColumnId) => boolean): ColumnDef<StatusRow>[] {
    const base: ColumnDef<StatusRow>[] = [
        { id: 'date', accessorKey: 'date', header: i18n.global.t(`${HEADINGS_KEY}.date`), enableSorting: true },
        // `barn`, not an ad hoc column id: `RecordsTable`'s shared `cellText`/`LOCATION_COLUMN_IDS`
        // already render a Farm/Barn chain and give it a width floor under this exact id (see
        // Sold/Slaughters).
        { id: 'barn', header: i18n.global.t(`${HEADINGS_KEY}.location`), meta: { filterable: true } },
    ];

    // Right after Location, not lumped in with the weight figures below: Section is a refinement of
    // where the registration happened, so it reads next to the column it narrows.
    if (isVisible('section')) {
        base.push({ id: 'section', header: i18n.global.t(HEADING_KEYS.section), meta: { filterable: true } });
    }

    base.push(
        { id: 'number', accessorKey: 'number', header: i18n.global.t(`${HEADINGS_KEY}.pigs`), enableSorting: true, meta: { align: 'right' } },
        { id: 'avg_weight', accessorKey: 'avg_weight', header: i18n.global.t(`${HEADINGS_KEY}.avgWeight`), enableSorting: true, meta: { align: 'right' } },
        { id: 'total_weight', accessorKey: 'total_weight', header: i18n.global.t(`${HEADINGS_KEY}.totalWeight`), enableSorting: true, meta: { align: 'right' } }
    );

    if (isVisible('created_by')) {
        base.push({ id: 'created_by', accessorKey: 'created_by', header: i18n.global.t(HEADING_KEYS.created_by), enableSorting: true });
    }

    return [...base, { id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } }];
}

/**
 * The Status page's configuration of the shared records page, plus what neither the shared kit nor
 * `RecordsTable` provide on their own: the column-visibility control over its optional columns (Section, and Created by which is off by default).
 *
 * No chart, unlike every other records page under Pigs - a status registration has no dedicated
 * chart to draw, so `useRecordsPage`'s own bins are left unread here. `avg_weight`/`total_weight` are
 * already the figures the shared footer totals read (unlike `SlaughterRow`, which carries two
 * independent weight pairs and nulls them out), so this page reuses `page.totals` unchanged rather
 * than forking its own `totalsSubheader`.
 */
export function useStatusPage() {
    const page = useRecordsPage<StatusRow>({
        records: { use: useStatusRecords, rootKey: queryKeys.getStatusRoot },
        facets,
    });

    const presence = computed(() => columnPresence(page.rows.value));

    /**
     * Drops Section's own active filter selection once its column is no longer on the table - a
     * hidden column has no chip left to clear it from, and `applyFacetFilters` would otherwise keep
     * matching rows against a filter the reader can no longer see or reach.
     */
    function clearFilterIfHidden(id: StatusToggleColumnId) {
        if (id in page.columnFilters.value) {
            const next = { ...page.columnFilters.value };

            delete next[id];
            page.columnFilters.value = next;
        }
    }

    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<StatusToggleColumnId>({
        storageKey: 'pigs.statusRecords.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: (id) => isColumnAvailable(id, presence.value),
        defaultVisible: (id) => id !== 'created_by',
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
        onHide: clearFilterIfHidden,
    });

    // A period change (or any other change to the loaded set) can drop Section's only data, taking
    // its facet option away with it - not just a user's own toggle.
    watch(presence, () => {
        if (!isColumnAvailable('section', presence.value)) {
            clearFilterIfHidden('section');
        }
    });

    return {
        ...page,
        columns: computed(() => columns(isVisible)),
        columnToggleOptions,
        toggleColumn,
        setAllColumns,
    };
}
